import {inject, Inject, Injectable, signal} from "@angular/core";
import {HttpClient, HttpEventType} from "@angular/common/http";
import {AllMasterDatabaseModel} from "../models/AllMasterDatabase-model";
import {SearchFilterModel} from "../models/SearchFilter-model";
import {UpdateMasterModel} from "../models/UpdateMaster-model";
import {forkJoin, map, Observable} from "rxjs";
import {DeleteDialogComponent} from "../helpers/delete-dialog/delete-dialog.component";
import {MatDialog} from "@angular/material/dialog";
import {DeleteFileFromDatabaseService} from "./DeleteFileFromDatabase.service";
import {NotificationService} from "./Notification.service";
import {VersionRecordsModel} from "../models/VersionRecordsModel";
import {DownloadFileFromDatabaseService} from "./DownloadFileFromDatabase.service";
import {EditMasterDialogComponent} from "../helpers/edit-master-dialog/edit-master-dialog.component";
import {CarImageService} from "./CarImage.service";
import {SortDirectionValuesModel} from "../models/SortDirectionValues-model";
import {CustomMasterUploadFileValidations} from "../master-details-wrapper/master-details/master-details.component";
import {FileUploadService} from "./fileUpload.service";
import {Location} from "@angular/common";
import {MasterHistoryModel} from "../models/MasterHistory-model";
import {normalizeMasterFilters} from "../shared/constants/master-filter-fields";
import {
    MasterHistoryDialogComponent
} from "../master-details-wrapper/master-details/master-history-dialog/master-history-dialog.component";


interface MasterPage {
    content: AllMasterDatabaseModel[];
    last?: boolean;
}

@Injectable({
    providedIn: "root"
})
export class AllMastersDatabaseService {
    private url = `${this.API_URL}`;

    public allMasters = signal<AllMasterDatabaseModel[]>([]);
    public faultyMasters = signal<AllMasterDatabaseModel[]>([]);
    public masterItem = signal<AllMasterDatabaseModel>({} as AllMasterDatabaseModel);
    public expandedMaster = signal<AllMasterDatabaseModel | null>(null);
    public masterItems = signal<AllMasterDatabaseModel>(new AllMasterDatabaseModel());
    public displayedColumns = signal<(keyof AllMasterDatabaseModel)[]>([]);
    public displayedTableHeaders = signal<string[]>([]);
    public versionItems = signal<VersionRecordsModel>(new VersionRecordsModel());
    public displayedVersionColumns = signal<(keyof VersionRecordsModel)[]>([]);

    public sortDirectionValues = signal<SortDirectionValuesModel>({active: "id", direction: "desc"});
    private filesToDownload = signal<number[]>([]);
    public searchString = signal('');
    public selectedFilters = signal<SearchFilterModel[]>([]);
    public searchedResults = signal<AllMasterDatabaseModel[]>([]);
    public pageNumber = signal<number>(0);
    public pageSize = signal<number>(50);
    private sortAttribute = signal<string>('id');
    private hasMorePages = true;
    private isFetchingPage = false;
    private isFetchingFaultyPage = false;
    private hasMoreFaultyPages = true;
    private faultyRequestId = 0;
    private activePageRequestId = 0;
    public masterId = signal<number>(-1);
    public ecuBuild = signal<string>('');
    public ecuProducer = signal<string>('');

    public isTableLoading = signal<boolean>(false);
    public isTableEmpty = signal<boolean>(false);
    public faultyMastersError = signal<boolean>(false);
    public openAdvanceSettings = signal<boolean>(false);
    public masterDetailsIsLoading = signal<boolean>(false);

    public myInputs = new Map<string, CustomMasterUploadFileValidations>([
        ["ini", {
            message: '',
            hasError: false,
        }]
    ])

    public progressWidth = signal<number>(0);
    public resetSignal = signal<boolean>(false);
    public inputText = "Drag & Drop INI file";

    private httpClient = inject(HttpClient);
    private carImageService = inject(CarImageService);
    private dialog = inject(MatDialog);
    private deleteFileFromDatabaseService = inject(DeleteFileFromDatabaseService);
    private notificationService = inject(NotificationService);
    private downloadService = inject(DownloadFileFromDatabaseService);
    private fileUploadService = inject(FileUploadService);
    private location = inject(Location);

    constructor(@Inject('API_URL') private API_URL: string) {
    }

    getAllTableHeaderNames() {
        let objectKeys = Object.keys(this.masterItems());
        for (let key of objectKeys) {
            this.displayedColumns().push(key as keyof AllMasterDatabaseModel);
        }
        this.displayedTableHeaders.set(['arrow', ...this.displayedColumns(), 'actions']);
    }

    getAllVersionHeaderNames() {
        let objKeys = Object.keys(this.versionItems());
        for (let key of objKeys) {
            this.displayedVersionColumns().push(key as keyof VersionRecordsModel);
        }
    }


    triggerAllMasterRecordsRequest(): Observable<MasterPage> {
        return this.httpClient.get<MasterPage>(`${this.url}/master/all`, {
            params: {
                pageNumber: this.pageNumber(),
                pageSize: this.pageSize(),
                sortAttribute: this.sortDirectionValues().active,
                sortDirection: this.sortDirectionValues().direction
            }
        });
    }

    getAllMasterRecords(): void {
        this.selectedFilters.set([]);
        this.searchString.set('');
        this.loadMasterPage(false);
    }

    applyFilters(filters: SearchFilterModel[]): void {
        this.selectedFilters.set(normalizeMasterFilters(filters));
        this.searchString.set('');
        this.loadMasterPage(false);
    }

    reloadCurrentMasterRecords(): void {
        if (this.searchString().trim().length >= 3 && this.selectedFilters().length === 0) {
            this.loadSearchPage(false);
        } else {
            this.loadMasterPage(false);
        }
    }

    private loadMasterPage(append: boolean): void {
        if (append && (this.isFetchingPage || !this.hasMorePages)) return;
        if (!append) {
            this.pageNumber.set(0);
            this.hasMorePages = true;
            this.allMasters.set([]);
            this.isTableEmpty.set(false);
            this.isTableLoading.set(true);
        }
        const requestId = ++this.activePageRequestId;
        this.isFetchingPage = true;
        const filters = this.selectedFilters();
        const request = filters.length > 0
            ? this.getAllMastersAdvancedFiltered(filters)
            : this.triggerAllMasterRecordsRequest();
        request.subscribe({
            next: page => {
                if (requestId !== this.activePageRequestId) return;
                const records = page.content ?? [];
                this.allMasters.set(append ? [...this.allMasters(), ...records] : records);
                this.hasMorePages = page.last === undefined ? records.length === this.pageSize() : !page.last;
                this.isTableEmpty.set(this.allMasters().length === 0);
                this.isFetchingPage = false;
                this.isTableLoading.set(false);
            },
            error: error => this.handlePageError(error, append, requestId)
        });
    }

    private handlePageError(error: any, append: boolean, requestId: number): void {
        if (requestId !== this.activePageRequestId) return;
        if (append) {
            this.pageNumber.update(page => page - 1);
        } else {
            this.allMasters.set([]);
            this.isTableEmpty.set(true);
        }
        this.isFetchingPage = false;
        this.isTableLoading.set(false);
        this.notificationService.showErrorMessage(
            error.status === 409 ? 'This attribute does not exist in this table' : 'Something went wrong',
            error.status === 409 ? 'Error' : 'Error Occurred'
        );
        if (error.status === 409) {
            this.sortDirectionValues.set({active: 'id', direction: 'desc'});
        }
    }

    onScroll(): void {
        if (this.isFetchingPage || !this.hasMorePages || this.isTableLoading()) return;
        this.increasePageNumber();
        if (this.searchString().trim().length >= 3 && this.selectedFilters().length === 0) {
            this.loadSearchPage(true);
        } else {
            this.loadMasterPage(true);
        }
    }


    onScrollFaulty() {
        if (this.isFetchingFaultyPage || !this.hasMoreFaultyPages || this.isTableLoading() || this.faultyMastersError()) return;
        this.increasePageNumber();
        this.getFaultyMasters();
    }

    retryFaultyMastersPage() {
        this.faultyMastersError.set(false);
        this.onScrollFaulty();
    }

    getAllMastersFilteredByMasterWinOls(winOls: string): Observable<MasterPage> {
        return this.httpClient.get<MasterPage>(`${this.url}/master/searchByWinOls`, {
            params: {
                pageNumber: this.pageNumber(),
                pageSize: this.pageSize(),
                winOlsReference: winOls
            }
        });
    }

    getAllMastersFilteredByVersionWinOls(winOls: string): Observable<MasterPage> {
        return this.httpClient.get<MasterPage>(`${this.url}/master/searchByVersionWinOls`, {
            params: {
                pageNumber: this.pageNumber(),
                pageSize: this.pageSize(),
                versionWinOls: winOls
            }
        });
    }

    getAllMastersByWinOls(winOls: string): void {
        this.searchString.set(winOls.trim());
        this.selectedFilters.set([]);
        this.loadSearchPage(false);
    }

    getAllMastersByWinOlsScrollable(_winOls: string): void {
        this.loadSearchPage(true);
    }

    private loadSearchPage(append: boolean): void {
        if (append && (this.isFetchingPage || !this.hasMorePages)) return;
        if (!append) {
            this.pageNumber.set(0);
            this.hasMorePages = true;
            this.allMasters.set([]);
            this.isTableEmpty.set(false);
            this.isTableLoading.set(true);
        }
        const requestId = ++this.activePageRequestId;
        this.isFetchingPage = true;
        const winOls = this.searchString();
        forkJoin([
            this.getAllMastersFilteredByMasterWinOls(winOls),
            this.getAllMastersFilteredByVersionWinOls(winOls)
        ]).subscribe({
            next: ([masterPage, versionPage]) => {
                if (requestId !== this.activePageRequestId) return;
                const records = this.mergeMasterLists(masterPage.content ?? [], versionPage.content ?? []);
                this.allMasters.set(append ? this.mergeMasterLists(this.allMasters(), records) : records);
                const masterLast = masterPage.last ?? (masterPage.content ?? []).length < this.pageSize();
                const versionLast = versionPage.last ?? (versionPage.content ?? []).length < this.pageSize();
                this.hasMorePages = !(masterLast && versionLast);
                this.isTableEmpty.set(this.allMasters().length === 0);
                this.isFetchingPage = false;
                this.isTableLoading.set(false);
            },
            error: error => this.handlePageError(error, append, requestId)
        });
    }

    getHomeSearchResultsByWinOls(winOls: string) {
        const requests = [
            this.getAllMastersFilteredByMasterWinOls(winOls),
            this.getAllMastersFilteredByVersionWinOls(winOls)
        ]

        forkJoin(requests).subscribe(([masterList1, masterList2]) => {
            this.searchedResults.set(this.mergeMasterLists(masterList1['content'], masterList2['content']));
        })
    }

    mergeMasterLists(masterList1: AllMasterDatabaseModel[], masterList2: AllMasterDatabaseModel[]): AllMasterDatabaseModel[] {
        const masterMap = new Map<number, AllMasterDatabaseModel>();
        for (const master of masterList1) {
            masterMap.set(master.id, master);
        }
        for (const master of masterList2) {
            masterMap.set(master.id, master);
        }
        return Array.from(masterMap.values());
    }

    triggerMasterUpdateRequest(id: number, masterEl: UpdateMasterModel) {
        return this.httpClient.post<AllMasterDatabaseModel>(`${this.url}/master/updateMaster/${id}`, masterEl, {observe: "response"});
    }

    updateMasterElement(masterEl: UpdateMasterModel, winOls: string, id: number) {
        const dialogRef = this.dialog.open(EditMasterDialogComponent, {
            width: "100%",
            maxWidth: "100%",
            panelClass: "edit-dialog-v3-panel",
            data: {text: `${winOls}`, content: masterEl, id}
        });

        dialogRef.afterClosed().subscribe((data: { closed: boolean, content: UpdateMasterModel }) => {
            if (data.closed) {
                const element = data.content;
                this.triggerMasterUpdateRequest(id, element).subscribe(response => {
                    if (response.status === 200) {
                        const index = this.allMasters().findIndex((value) => value.id === id);
                        Object.assign(this.allMasters()[index], response.body);
                        this.notificationService.showSuccessMessage("Master successfully updated.", "Master updated");
                    } else {
                        this.notificationService.showErrorMessage("Something went wrong!", "Error");
                    }
                });
            }
        });
    }


    deleteMasterFromDatabase(id: number, tableName: string) {
        const dialogRef = this.dialog.open(DeleteDialogComponent, {
            width: "350px",
            data: "Are you sure you want to delete?"
        })

        dialogRef.afterClosed().subscribe(confirmed => {
            if (confirmed) {
                this.deleteFileFromDatabaseService.deleteFileFromDatabase(id, tableName).subscribe((response) => {
                    if (response.status === 200) {
                        this.allMasters.update((allMasters) => allMasters.filter((master) => master.id !== id));
                        this.notificationService.showSuccessMessage('Successfully deleted', `${response.body["message"]}`);
                    }
                }, error => {
                    this.notificationService.showErrorMessage('Something went wrong', `${error.error['message']}`);
                });
            }
        })
    }

    getAllMastersAdvancedFiltered(filters: SearchFilterModel[] = this.selectedFilters()): Observable<MasterPage> {
        return this.httpClient.post<MasterPage>(`${this.url}/master/listAllMastersFiltered`, normalizeMasterFilters(filters), {
            params: {
                pageNumber: this.pageNumber(),
                pageSize: this.pageSize(),
                sortAttribute: this.sortDirectionValues().active,
                sortDirection: this.sortDirectionValues().direction
            }
        });
    }

    getFaultyMasters() {
        if (this.isFetchingFaultyPage || !this.hasMoreFaultyPages) return;
        this.loadFaultyMasters(true);
    }

    getFaultyMastersFilteredByEcuBuildName() {
        this.pageNumber.set(0);
        this.faultyMasters.set([]);
        this.isTableEmpty.set(false);
        this.faultyMastersError.set(false);
        this.hasMoreFaultyPages = true;
        this.loadFaultyMasters(false);
    }

    private loadFaultyMasters(append: boolean) {
        const requestId = ++this.faultyRequestId;
        this.isFetchingFaultyPage = true;
        this.isTableLoading.set(true);
        this.faultyMastersError.set(false);
        this.httpClient.get<MasterPage>(`${this.url}/master/faultyMasters`, {
            params: {
                pageNumber: this.pageNumber(),
                pageSize: this.pageSize(),
                sortAttribute: this.sortAttribute(),
                sortDirection: this.sortDirectionValues().direction
            }
        }).subscribe({
            next: response => {
                if (requestId !== this.faultyRequestId) return;
                const records = response.content ?? [];
                this.faultyMasters.set(append ? [...this.faultyMasters(), ...records] : records);
                this.hasMoreFaultyPages = response.last === undefined
                    ? records.length === this.pageSize()
                    : !response.last;
                this.isTableEmpty.set(this.faultyMasters().length === 0);
                this.isFetchingFaultyPage = false;
                this.isTableLoading.set(false);
            },
            error: () => {
                if (requestId !== this.faultyRequestId) return;
                if (append) this.pageNumber.update(page => Math.max(0, page - 1));
                this.faultyMastersError.set(true);
                this.isFetchingFaultyPage = false;
                this.isTableLoading.set(false);
            }
        });
    }

    updateFaultyMaster(element: UpdateMasterModel, winOls: string, id: number) {
        const dialogRef = this.dialog.open(EditMasterDialogComponent, {
            width: "100%",
            maxWidth: "100%",
            panelClass: "edit-dialog-v3-panel",
            data: {text: `${winOls}`, content: element, id}
        });

        dialogRef.afterClosed().subscribe((data: { closed: boolean, content: UpdateMasterModel }) => {
            if (data.closed) {
                const masterEl = data.content;
                this.triggerMasterUpdateRequest(id, masterEl).subscribe(response => {
                    if (response.status === 200) {
                        this.faultyMasters.update((faultyMasters) => faultyMasters.filter((master) => master.id !== id));
                        this.notificationService.showSuccessMessage("Faulty Master successfully updated.", "Faulty Master updated.");
                    } else {
                        this.notificationService.showErrorMessage("Something went wrong.", "An Error Occurred.");
                    }
                });
            }
        });
    }


    deleteFaultyMaster(id: number) {
        const dialogRef = this.dialog.open(DeleteDialogComponent, {
            width: "350px",
            data: "Are you sure you want to delete?"
        })

        dialogRef.afterClosed().subscribe(confirmed => {
            if (confirmed) {
                this.deleteFileFromDatabaseService.deleteFileFromDatabase(id, 'masters').subscribe(response => {
                    if (response.status === 200) {
                        this.faultyMasters.update((faultyMasters) => faultyMasters.filter((master) => master.id !== id));
                        this.notificationService.showSuccessMessage("Successfully Deleted", `Faulty master ${id} deleted from table.`);
                    }
                }, error => {
                    this.notificationService.showErrorMessage("Something went wrong", `${error.error["message"]}`);
                })

            }
        })
    }

    downloadMastersFromTable(id: number, winOls: string, tableName: string) {
        this.filesToDownload.update((prevIds) => [...prevIds, id]);

        const toastObj = this.notificationService.showInfoMessage(`Preparing your ${winOls} file...`, 'Preparing..');

        this.downloadService.downloadFileFromDatabase(id, tableName).subscribe(response => {
            const index = this.filesToDownload().indexOf(id);

            this.filesToDownload.update((files) => files.filter((file, i) => i !== index));

            if (response.status === 200) {

                this.notificationService.clear(toastObj.toastId);

                window.location.href = response.body.url;

                this.notificationService.showSuccessMessage(`<div><span>Your file ${winOls} is downloaded</span></div>`, "Success");

            } else {
                this.notificationService.showErrorMessage('Something went wrong with file.', 'Failed');
            }
            if (response.status === 500) {
                this.notificationService.showErrorMessage("You can't download this file. ", 'Download failed');
            }
        })
    }

    isDownloadInProgress(id: number) {
        return this.filesToDownload().includes(id);
    }

    checkIfEcuIsValid(ecuBuild: string) {
        return this.httpClient.post<{
            valid: boolean
        }>(`${this.url}/ecuBuildNames/verify`, {ecuBuild: ecuBuild}).pipe(map(value => {
            if (value.valid) {
                return null;
            } else {
                return {valid: true}
            }
        }));
    }

    getMasterById(): Observable<AllMasterDatabaseModel> {
        this.masterDetailsIsLoading.set(true);
        return this.httpClient.get<AllMasterDatabaseModel>(`${this.url}/master/${this.masterId()}`).pipe(
            map(value => {
                this.masterItem.set(value as AllMasterDatabaseModel);
                this.ecuBuild.set(value.ecuBuild);
                this.ecuProducer.set(value.ecuProducer);
                this.carImageService.getImage({
                    make: value.vehicleProducer,
                    modelFamily: value.vehicleSeries,
                    modelYear: value.vehicleModelYear,
                    modelRange: value.vehicleModel,
                    width: '1000'
                });
                this.masterDetailsIsLoading.set(false);
                return value;
            })
        );
    }

    updateMasterDetails(masterItem: AllMasterDatabaseModel, id: number) {
        const dialogRef = this.dialog.open(EditMasterDialogComponent, {
            width: "100%",
            maxWidth: "100%",
            panelClass: "edit-dialog-v3-panel",
            data: {text: `${masterItem.winOLSReference}`, content: masterItem as UpdateMasterModel, id}
        });

        dialogRef.afterClosed().subscribe((data: { closed: boolean, content: UpdateMasterModel }) => {
            if (data.closed) {
                const updateMasterElement = data.content;
                this.triggerMasterUpdateRequest(masterItem.id, updateMasterElement).subscribe(response => {
                    this.masterItem.set(response.body);
                });
            }
        });
    }


    deleteMasterItem(masterItemId: number, tableName: string) {
        const dialogRef = this.dialog.open(DeleteDialogComponent, {
            width: "350px",
            data: "Are you sure you want to delete?"
        })

        dialogRef.afterClosed().subscribe(confirmed => {
            if (confirmed) {
                this.deleteFileFromDatabaseService.deleteFileFromDatabase(masterItemId, tableName).subscribe((response) => {
                    if (response.status === 200) {
                        this.allMasters.update((allMasters) => allMasters.filter((master) => master.id !== masterItemId));
                        this.notificationService.showSuccessMessage('Successfully deleted', `${response.body["message"]}`);
                        this.location.back();
                    }
                }, error => {
                    this.notificationService.showErrorMessage('Something went wrong', `${error.error['message']}`);
                });
            }
        })
    }

    masterExists(id: number) {
        return this.httpClient.get(`${this.url}/master/${id}`, {observe: "response"});
    }

    toggleExpandedMasterRow(expandedDetail: AllMasterDatabaseModel) {
        if (expandedDetail == this.expandedMaster()) {
            this.expandedMaster.set(null);
        } else {
            this.expandedMaster.set(expandedDetail);
        }
    }


    uploadMasterIniFile(file: File) {
        if (file) {
            this.myInputs.set('ini', {
                message: '',
                hasError: false,
            })
            this.fileUploadService.uploadMasterIniFile(file, this.masterId()).subscribe((response) => {
                    if (response.type === HttpEventType.UploadProgress) {
                        this.progressWidth.set(Math.round(response.loaded / response.total * 100));
                    } else if (response.type === HttpEventType.Response)
                        if (response.status === 200) {
                            let body = response.body as { masterRecords: AllMasterDatabaseModel, message: string };
                            this.masterItem.set(body.masterRecords);
                            this.notificationService.showSuccessMessage(`<div><span>Master Ini file uploaded.</span></div>`, "Success");
                            this.refreshIniFile();
                            this.getAllMasterRecords();
                            this.getFaultyMastersFilteredByEcuBuildName()
                            this.progressWidth.set(0);
                        }
                }, error => {
                    this.myInputs.set('ini', {
                        message: error.error.message,
                        hasError: true
                    })
                    this.progressWidth.set(0);
                }
            )
        }
    }

    getMasterStatisticsHistory(masterId: number) {
        return this.httpClient.get<MasterHistoryModel[]>(`${this.url}/master/${masterId}/workHistory`, {observe: "response"});
    }

    showMasterStatisticsHistory(masterId: number, masterHistoryModel: MasterHistoryModel[]) {
        this.dialog.open(MasterHistoryDialogComponent, {
            width: "720px",
            maxWidth: "calc(100vw - 32px)",
            maxHeight: "calc(100dvh - 32px)",
            panelClass: "history-dialog-panel",
            data: {text: `${masterId}`, masterId, content: masterHistoryModel}
        });
    }

    refreshIniFile() {
        this.resetSignal.set(true);
        this.myInputs = new Map<string, CustomMasterUploadFileValidations>([
            ["ini", {
                message: '',
                hasError: false,
            }],
        ]);
    }

    increasePageNumber() {
        this.pageNumber.update((pageNumber) => pageNumber + 1);
    }

    restartTableValues() {
        this.faultyRequestId++;
        this.isFetchingFaultyPage = false;
        this.hasMoreFaultyPages = true;
        this.faultyMastersError.set(false);
        this.isTableLoading.set(false);
        this.isTableEmpty.set(false);
        this.pageNumber.set(0);
        this.pageSize.set(50);
        this.faultyMasters.set([]);
        this.displayedColumns.set([]);
        this.displayedTableHeaders.set([]);
        this.displayedVersionColumns.set([]);
    }
    resetSortDirection() {
        this.sortDirectionValues.set({
            active: '',
            direction: ''
        });
    }

    // clearErrorState() {
    //     this.isTableLoading.set(false);
    //     this.isTableEmpty.set(false);
    //     this.resetSortDirection();
    //     this.notificationService.clearAll();
    // }

}
